import type { ColumnMetadata } from '../types';

export const isBooleanColumnMetadata = (metadata?: {
  type?: string;
  dataType?: string;
}): boolean => {
  const type = metadata?.type || metadata?.dataType;
  return type === 'boolean';
};

/** Whether a stored grid/form value should render as checked. */
export const isBooleanValueTrue = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  if (!normalized) return false;

  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

/** Normalized in-memory checkbox value ('1' / '0'), aligned with the legacy grid. */
export const toBooleanStorageValue = (value: unknown): '1' | '0' =>
  isBooleanValueTrue(value) ? '1' : '0';

/** Initial checkbox value for a new row/form field. */
export const getBooleanInitialValue = (defaultValue?: string | null): '1' | '0' => {
  if (defaultValue == null || defaultValue === '') return '0';
  return toBooleanStorageValue(defaultValue);
};

/** String sent to Grid API so backend bool.TryParse succeeds ('true' / 'false'). */
export const toBooleanApiValue = (value: unknown): 'true' | 'false' =>
  isBooleanValueTrue(value) ? 'true' : 'false';

export const getInitialFieldValue = (col: ColumnMetadata): unknown => {
  const fieldName = col.field || col.dataField;
  if (!fieldName) return undefined;

  if (isBooleanColumnMetadata(col)) {
    return getBooleanInitialValue(col.defaultValue);
  }

  return col.defaultValue ?? '';
};

export const formatFieldValueForSave = (
  value: unknown,
  metadata?: ColumnMetadata,
  normalizeMedia?: (value: string) => string
): string => {
  if (isBooleanColumnMetadata(metadata)) {
    return toBooleanApiValue(value);
  }

  const stringValue = value != null ? String(value) : '';
  return normalizeMedia ? normalizeMedia(stringValue) : stringValue;
};

/**
 * Normalize a string value for filter comparison against defaultFilters.Value.
 * Backend serializes booleans in Fields.StringValue inconsistently ("1"/"", "True"/"False", etc.)
 * while defaultFilters.Value comes as "true"/"false" from the backend DTO.
 * Non-boolean values are lowercased for case-insensitive comparison.
 */

const BOOLEAN_LIKE_VALUES = new Set(['1', '0', 'true', 'false', 'yes', 'no', '']);

export const normalizeBooleanish = (raw: string): string => {
  const lower = raw.toLowerCase();
  if (!BOOLEAN_LIKE_VALUES.has(lower)) return lower;
  return isBooleanValueTrue(raw) ? 'true' : 'false';
};
